import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { syncDayjsLocale } from "../dayjsLocale/dayjsLocale";
import { fallbackLng, languages } from "../i18n.config";
import { BCP47LangCode } from "../i18n.types";

/**
 * Hook that exposes the current language, available languages, and a function
 * to switch languages. Useful for language-picker components and for passing
 * the resolved locale to formatting utilities.
 *
 * @example
 * ```tsx
 * const { currentLang, changeLanguage, languages } = useI18n();
 * ```
 */
export function useI18n() {
  const { i18n } = useTranslation();

  const currentLang = (i18n.resolvedLanguage ?? fallbackLng) as BCP47LangCode;

  const changeLanguage = useCallback(
    async (lang: BCP47LangCode) => {
      await i18n.changeLanguage(lang);
      // Keep dayjs (relative time / localized dates) in sync with the UI language.
      await syncDayjsLocale(lang);
    },
    [i18n],
  );

  return {
    /** The BCP 47 language tag currently in effect. */
    currentLang,
    /** The default / fallback language. */
    defaultLang: fallbackLng,
    /** All supported languages. */
    languages,
    /** Switch the active language (persisted to localStorage / cookie). */
    changeLanguage,
  };
}
